import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { AGENTS } from '../../lib/brand';
import { ease, duration } from '../../lib/motionConfig';

/**
 * AgentOrchestrationGraph — M6 "Wow Moment" (redesigned)
 *
 * Two-node orchestration illustration:
 *   Cognara (Orchestrator, Ember, left)  ─signal arc─▶  Kāryakartā (Executor, Mint, right)
 *
 * Pure SVG + Framer Motion. Respects `prefers-reduced-motion`.
 *
 * Props:
 *   active        — boolean. Animates concentric rings + signal pulses when true.
 *   activeAgent   — 'sutradhar' | 'karyakarta'. Which node is "currently acting".
 *   tools         — string[]. Optional list of active tool names; shown as
 *                   chips near Kāryakartā when executor is active.
 *   size          — 'compact' (default, 360×180) | 'large' (520×260).
 *   className     — extra wrapper classes.
 */

const NODES = {
  sutradhar: {
    id: 'sutradhar',
    xPct: 22,
    color: 'rgba(242,107,31,1)',    // ember
    soft:  'rgba(242,107,31,0.16)',
    glow:  'rgba(242,107,31,0.45)',
    label: AGENTS.sutradhar.display,
    role:  AGENTS.sutradhar.role,
  },
  karyakarta: {
    id: 'karyakarta',
    xPct: 78,
    color: 'rgba(110,231,183,1)',   // mint
    soft:  'rgba(110,231,183,0.16)',
    glow:  'rgba(110,231,183,0.45)',
    label: AGENTS.karyakarta.display,
    role:  AGENTS.karyakarta.role,
  },
};

const SIZE_MAP = {
  compact: { w: 360, h: 180, nodeR: 22, font: 12, roleFont: 9.5 },
  large:   { w: 520, h: 260, nodeR: 32, font: 14, roleFont: 11 },
};

export const AgentOrchestrationGraph = ({
  active = false,
  activeAgent = 'sutradhar',
  tools = [],
  size = 'compact',
  className = '',
}) => {
  const { w, h, nodeR, font, roleFont } = SIZE_MAP[size] || SIZE_MAP.compact;
  const prefersReduced = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const cy = h * 0.5;
  const sx = (NODES.sutradhar.xPct / 100) * w;
  const kx = (NODES.karyakarta.xPct / 100) * w;

  // Gentle upward arc between the two nodes (control point raised by 35% of height)
  const controlY = cy - h * 0.22;
  const controlX = (sx + kx) / 2;
  const arcPath = `M ${sx} ${cy} Q ${controlX} ${controlY} ${kx} ${cy}`;

  // Arrow head points from arc direction at target
  const tangentX = kx - controlX;
  const tangentY = cy - controlY;
  const arrowAngle = (Math.atan2(tangentY, tangentX) * 180) / Math.PI;

  const sActive = active && activeAgent === 'sutradhar';
  const kActive = active && activeAgent === 'karyakarta';

  return (
    <div
      className={`relative ${className}`}
      style={{ width: w, height: h }}
      data-testid="agent-orchestration-graph"
      role="img"
      aria-label={`Agent orchestration — ${activeAgent} active`}
    >
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        className="absolute inset-0 overflow-visible"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="ig-sutradhar-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={NODES.sutradhar.glow} stopOpacity="0.7" />
            <stop offset="100%" stopColor={NODES.sutradhar.glow} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="ig-karyakarta-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={NODES.karyakarta.glow} stopOpacity="0.7" />
            <stop offset="100%" stopColor={NODES.karyakarta.glow} stopOpacity="0" />
          </radialGradient>
          <linearGradient id="ig-arc" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="rgba(242,107,31,0.75)" />
            <stop offset="100%" stopColor="rgba(110,231,183,0.75)" />
          </linearGradient>
          {/* Arrow head marker — defined as triangle, rotated at runtime */}
        </defs>

        {/* ============ SIGNAL ARC ============ */}
        <path
          d={arcPath}
          fill="none"
          stroke="url(#ig-arc)"
          strokeWidth={1.25}
          strokeOpacity={active ? 0.7 : 0.3}
          strokeDasharray="4 5"
          strokeLinecap="round"
        />

        {/* Arrow head at Kāryakartā */}
        <g transform={`translate(${kx - nodeR - 6} ${cy}) rotate(${arrowAngle})`}>
          <path
            d="M -6 -4 L 0 0 L -6 4 Z"
            fill={NODES.karyakarta.color}
            opacity={active ? 0.85 : 0.5}
          />
        </g>

        {/* Traveling signal pulses along the arc */}
        {active && !prefersReduced && (
          <>
            <circle r={2.8} fill={NODES.sutradhar.color}>
              <animateMotion dur="2.4s" repeatCount="indefinite" path={arcPath} />
              <animate attributeName="opacity" values="0;1;1;0" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle r={2} fill={NODES.karyakarta.color} opacity="0.8">
              <animateMotion dur="2.4s" begin="1.1s" repeatCount="indefinite" path={arcPath} />
              <animate attributeName="opacity" values="0;0.9;0.9;0" dur="2.4s" begin="1.1s" repeatCount="indefinite" />
            </circle>
          </>
        )}

        {/* ============ COGNARA NODE ============ */}
        <g>
          {/* Breathing glow halo */}
          {sActive && !prefersReduced && (
            <motion.circle
              cx={sx} cy={cy} r={nodeR * 1.2}
              fill="url(#ig-sutradhar-glow)"
              initial={{ opacity: 0.3, scale: 0.85 }}
              animate={{ opacity: [0.3, 0.75, 0.3], scale: [0.85, 1.6, 0.85] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ transformOrigin: `${sx}px ${cy}px` }}
            />
          )}
          {/* Outer ring */}
          <motion.circle
            cx={sx} cy={cy} r={nodeR}
            fill="none"
            stroke={NODES.sutradhar.color}
            strokeWidth={sActive ? 1.8 : 1.2}
            strokeOpacity={sActive ? 0.9 : 0.55}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: duration.medium, ease: ease.entrance }}
            style={{ transformOrigin: `${sx}px ${cy}px` }}
          />
          {/* Inner filled disc */}
          <motion.circle
            cx={sx} cy={cy} r={nodeR * 0.58}
            fill={NODES.sutradhar.soft}
            stroke={NODES.sutradhar.color}
            strokeWidth={1.2}
            strokeOpacity={0.85}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: duration.medium, ease: ease.entrance, delay: 0.08 }}
            style={{ transformOrigin: `${sx}px ${cy}px` }}
          />
          {/* Core dot */}
          <circle cx={sx} cy={cy} r={3.5} fill={NODES.sutradhar.color} />
        </g>

        {/* ============ KĀRYAKARTĀ NODE ============ */}
        <g>
          {kActive && !prefersReduced && (
            <motion.circle
              cx={kx} cy={cy} r={nodeR * 1.2}
              fill="url(#ig-karyakarta-glow)"
              initial={{ opacity: 0.3, scale: 0.85 }}
              animate={{ opacity: [0.3, 0.75, 0.3], scale: [0.85, 1.6, 0.85] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ transformOrigin: `${kx}px ${cy}px` }}
            />
          )}
          <motion.circle
            cx={kx} cy={cy} r={nodeR}
            fill="none"
            stroke={NODES.karyakarta.color}
            strokeWidth={kActive ? 1.8 : 1.2}
            strokeOpacity={kActive ? 0.9 : 0.55}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: duration.medium, ease: ease.entrance, delay: 0.06 }}
            style={{ transformOrigin: `${kx}px ${cy}px` }}
          />
          <motion.circle
            cx={kx} cy={cy} r={nodeR * 0.58}
            fill={NODES.karyakarta.soft}
            stroke={NODES.karyakarta.color}
            strokeWidth={1.2}
            strokeOpacity={0.85}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: duration.medium, ease: ease.entrance, delay: 0.14 }}
            style={{ transformOrigin: `${kx}px ${cy}px` }}
          />
          <circle cx={kx} cy={cy} r={3.5} fill={NODES.karyakarta.color} />
        </g>
      </svg>

      {/* =========== LABELS (HTML — a11y + sharp fonts) =========== */}
      {[
        { node: NODES.sutradhar, x: sx, isActive: sActive },
        { node: NODES.karyakarta, x: kx, isActive: kActive },
      ].map(({ node, x, isActive }) => (
        <div
          key={`label-${node.id}`}
          className="absolute text-center pointer-events-none"
          style={{
            left: x,
            top: cy + nodeR + 14,
            transform: 'translateX(-50%)',
            lineHeight: 1.15,
          }}
          data-testid={`agent-node-${node.id}`}
        >
          <div
            className="font-semibold tracking-tight whitespace-nowrap"
            style={{
              fontSize: font,
              color: isActive ? node.color : 'var(--nexus-text-primary)',
              textShadow: isActive ? `0 0 14px ${node.soft}` : 'none',
              transition: 'color 280ms',
            }}
          >
            {node.label}
          </div>
          <div
            className="ig-eyebrow mt-0.5"
            style={{
              fontSize: roleFont,
              opacity: 0.65,
              color: 'var(--nexus-text-tertiary)',
              letterSpacing: '0.08em',
            }}
          >
            {node.role}
          </div>
        </div>
      ))}

      {/* Tool chips — shown near Kāryakartā when executor is active or tools are running */}
      {tools.length > 0 && (
        <div
          className="absolute flex flex-wrap gap-1 justify-center"
          style={{
            left: kx,
            top: cy - nodeR - 28,
            transform: 'translateX(-50%)',
            maxWidth: w * 0.4,
          }}
          aria-label="Active tools"
        >
          {tools.slice(0, 3).map((t, i) => (
            <motion.span
              key={`${t}-${i}`}
              initial={{ opacity: 0, y: 4, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: i * 0.06, duration: duration.short, ease: ease.entrance }}
              className="text-[8.5px] tracking-wider uppercase px-1.5 py-0.5 rounded-md"
              style={{
                background: NODES.karyakarta.soft,
                color: NODES.karyakarta.color,
                border: `1px solid ${NODES.karyakarta.color.replace(',1)', ',0.4)')}`,
              }}
            >
              {t}
            </motion.span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentOrchestrationGraph;
